// components/Footer.jsx — Premium Footer
import { useNavigate } from "react-router-dom";

export default function Footer() {
  const navigate = useNavigate();

  const navLinks = [
    { label: "Prices", path: "/prices" },
    { label: "About Us", path: "/about" },
    { label: "FAQs", path: "/faqs" },
    { label: "Contact", path: "/contact" },
  ];

  return (
    <footer className="relative bg-black overflow-hidden">
      {/* Gradient accent top */}
      <div className="h-px bg-gradient-to-r from-transparent via-[#00809D] to-transparent" />

      {/* Background glow effects */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[#00809D]/[0.04] rounded-full blur-[120px]" />
      <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-[#00809D]/[0.03] rounded-full blur-[80px] -translate-x-1/2 translate-y-1/2" />
      <div className="absolute bottom-0 right-0 w-[250px] h-[250px] bg-[#00a0c4]/[0.03] rounded-full blur-[80px] translate-x-1/3 translate-y-1/3" />

      <div className="relative z-10 max-w-6xl mx-auto px-6 lg:px-8">



        {/* Main Content */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 py-12 sm:py-16">

          {/* Brand Column */}
          <div className="md:col-span-5 space-y-5">
            <button onClick={() => navigate('/')} className="group">
              <img src="/logo-adsurg.png" alt="Adsurg" className="h-20 lg:h-24 w-auto object-contain group-hover:scale-105 transition-all duration-300" />
            </button>
            <p className="text-white/60 text-sm leading-relaxed max-w-xs">
              Data-driven advertising solutions that scale your business across every major platform. Trusted by 150+ brands worldwide.
            </p>

            {/* Social Icons */}
            <div className="flex gap-2.5">
              {[
                { label: "LinkedIn", href: "https://www.linkedin.com/company/adsurg/about/", d: "M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 1 1 0-4.125 2.062 2.062 0 0 1 0 4.125zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" },
              ].map((s, i) => (
                <a key={i} href={s.href} target={s.href !== "#" ? "_blank" : "_self"} rel={s.href !== "#" ? "noopener noreferrer" : ""} aria-label={s.label}
                  className="w-9 h-9 rounded-lg bg-white/[0.05] border border-white/[0.08] flex items-center justify-center text-white/60 hover:text-white hover:bg-[#00809D]/20 hover:border-[#00809D]/40 transition-all duration-300">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d={s.d} /></svg>
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links Column */}
          <div className="md:col-span-3">
            <h4 className="text-white text-sm font-semibold uppercase tracking-wider mb-5">Quick Links</h4>
            <ul className="space-y-3">
              {navLinks.map((link, i) => (
                <li key={i}>
                  <button onClick={() => navigate(link.path)}
                    className="text-white/70 text-sm hover:text-white hover:translate-x-1 transition-all duration-300 flex items-center gap-2 group">
                    <svg className="w-3 h-3 text-[#00809D] opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info Column */}
          <div className="md:col-span-4">
            <h4 className="text-white text-sm font-semibold uppercase tracking-wider mb-5">Get in Touch</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3 group">
                <div className="w-8 h-8 rounded-lg bg-[#00809D]/10 border border-[#00809D]/15 flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:bg-[#00809D]/20 transition-colors">
                  <svg className="w-4 h-4 text-[#00809D]" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                </div>
                <div>
                  <p className="text-xs text-white/40 mb-0.5">Contact</p>
                  <button
                    type="button"
                    onClick={() => navigate("/contact")}
                    className="text-sm text-white/70 group-hover:text-white transition-colors"
                  >
                    Use Contact Form
                  </button>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/[0.06] py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-white/50 text-xs">
            © {new Date().getFullYear()} Adsurg. All rights reserved.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button onClick={() => navigate('/refund-policy')} className="text-white/50 text-xs hover:text-white transition-colors">Refund Policy</button>
            <button onClick={() => navigate('/terms')} className="text-white/50 text-xs hover:text-white transition-colors">Terms and Conditions</button>
          </div>
        </div>
      </div>
    </footer>
  );
}
